import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateService } from "@ngx-translate/core";
import { MarketsService, RetailersService, UsersService, RolesService, UserRequest, FilterRequestForCombo, LanguageResponse } from '@src/app/shared/backend/clients';
import { spacesValidator } from '@src/app/shared/validators/trimSpaces.validator';
import { firstValueFrom } from 'rxjs';
import { debounceTime, switchMap, distinctUntilChanged } from 'rxjs/operators';
import { SessionService } from '@src/app/shared/services/session.service';
import { FilterOperator } from '@app/shared/enums/FilterOperator.enum';
import { latinCharsetRegexValidator } from '@src/app/shared/validators/latinCharacters.validator';
import { emailRegexValidator } from '@src/app/shared/validators/email.validator';
import { Restrictions } from '@src/app/shared/enums/Restrictions.enum';
import { ComboData } from '@src/app/shared/components/comboData/comboData';
import { BaseViewComponent } from '@src/app/shared/components/base-list/base-view.component';

@Component({
    selector: 'app-add-user',
    templateUrl: './add-user.component.html',
    styleUrl: './add-user.component.scss',
    host: {
        class: 'app-add-user'
    },
    standalone: false
})
export class AddUserComponent extends BaseViewComponent implements OnInit {

  isSuperAdmin: boolean = false;

  isMarketsFieldDisabled = true;
  isRetailersFieldDisabled = true;
  displayChangePasswordBlock: boolean = false;
  filterOperator = FilterOperator;

  languages: LanguageResponse[] = [];
  language: any;

  combosData: { [key: string]: ComboData } = {
    'markets': new ComboData(async (filter) => await firstValueFrom(this.marketsService.apiMarketsFilterComboPost(filter))),
    'retailers': new ComboData(async (filter) => await firstValueFrom(this.retailersService.apiRetailersFilterComboPost(filter))),
    'roles': new ComboData(async (filter) => await firstValueFrom(this.rolesService.apiRolesFilterComboPost(filter)))
  }

  public userForm: FormGroup = this.fb.group({
    firstname: ['', [Validators.required, Validators.maxLength(100)]],
    lastname: ['', [Validators.required, Validators.maxLength(100)]],
    romanisedFirstName: ['', [latinCharsetRegexValidator()]],
    romanisedLastName: ['', [latinCharsetRegexValidator()]],
    roleId: [[], [Validators.required]],
    username: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(100), spacesValidator()]],
    email: ['', [Validators.required, emailRegexValidator(), Validators.maxLength(50)]],
    retailerIds: [[]],
    marketIds: [[]],
    allowPasswordChange: ['false'],
    changePassword: [false],
    // password: ['', [Validators.required, passwordRegexValidator()]],
    // confirmPassword: ['', [Validators.required, confirmPasswordValidator('password')]],
    isActive: [true],
    selectedLanguage: [[]]
  });

  constructor(
    private changeDetector: ChangeDetectorRef,
    private translate: TranslateService,
    private router: Router,
    private session: SessionService,
    private fb: FormBuilder,
    private rolesService: RolesService,
    private usersService: UsersService,
    private marketsService: MarketsService,
    private retailersService: RetailersService,
  ) { super(); }

  ngOnInit() {
    this.init();
  }

  private async init(): Promise<void> {
    this.getLanguages();
    this.handleUsernameField();

    await this.initCombosData();

    this.userForm.get('roleId')?.valueChanges.subscribe(roleIds => {
      this.userForm.patchValue(
        {
          marketIds: [],
          retailerIds: []
        },
        { emitEvent: false }
      );

      this.userForm.get('marketIds')?.markAsPristine();
      this.userForm.get('marketIds')?.markAsUntouched();

      this.userForm.get('retailerIds')?.markAsPristine();
      this.userForm.get('retailerIds')?.markAsUntouched();

      this.isMarketsFieldDisabled = true;
      this.isRetailersFieldDisabled = true;

      this.updateValidatorsBasedOnRoles(roleIds);
    });

    this.changeDetector.detectChanges();
  }

  private async initCombosData() {
    await this.combosData['markets'].getData(null, 1);

    await this.combosData['retailers'].getData(null, 1);

    this.combosData['roles'].additionalFilters = [
      { propertyName: 'isActive', operator: FilterOperator.EQUALS, values: ['true'] }
    ];
    await this.combosData['roles'].getData(null, 1);
  }

  async onLazyLoadComboData(comboType: 'markets' | 'retailers' | 'roles', event: any) {
    const search = event?.search?.trim() || null;
    const activePage = Math.max(1, (event?.pageIndex ?? 1));

    await this.combosData[comboType].getData(search, activePage);
  }

  getComboTotalPages(comboType: 'markets' | 'retailers' | 'roles'): number {
    const totalRecords = this.combosData[comboType]?.totalRecords ?? 0;
    const pageSize = Number(this.combosData[comboType]?.rowsPerPageOption) || 5;

    return Math.max(1, Math.ceil(totalRecords / pageSize));
  }

  handleUsernameField() {
    const usernameControl = this.userForm.get('username');

    usernameControl?.valueChanges.subscribe(value => {
      if (typeof value !== 'string') return;

      const trimmedValue = value.trim();

      if (value !== trimmedValue) {
        usernameControl.setValue(trimmedValue, { emitEvent: false });
      }
    });

    usernameControl?.valueChanges.pipe(
      debounceTime(500),
      distinctUntilChanged(),
      switchMap(async value => {
        if (typeof value === 'string' && value.trim()) {
          const payload : FilterRequestForCombo = {
            pageNumber : 1,
            pageSize : 1,
            additionalFilters : [
              {
                propertyName: 'username',
                operator: this.filterOperator.EQUALS,
                values: [value.trim()]
              }
            ]
          }

          return await firstValueFrom(this.usersService.apiUsersFilterComboPost(payload));
        }

        return { results: [] };
      })
    ).subscribe((usernameCheck: any) => {
      if ((usernameCheck?.results?.length ?? 0) > 0) {
        usernameControl?.setErrors({ usernameTaken: true });
      } else {
        usernameControl?.setErrors(null);
      }
    });
  }

  private getLanguages() {
    const languages = this.session.companySettings?.languages ?? [];
    const enabledLanguages = languages.filter(language => language.isEnabled);

    const translation = this.translate.instant('PORTAL_LANGUAGES');
    const transformedLanguages = enabledLanguages.map(lang => {
      console.log('Language:', lang);
      const code = lang.code || 'unknown';

      return {
        id: lang.id,
        language: translation[code.toUpperCase()],
        code: code
      };
    });

    this.languages = transformedLanguages as any;
  }

  private async updateValidatorsBasedOnRoles(roleIds: any): Promise<void> {
    const roleId = roleIds?.[0];

    if (!roleId) {
      this.isMarketsFieldDisabled = true;
      this.isRetailersFieldDisabled = true;
      return;
    }

    const roleRestrictions = await firstValueFrom(this.rolesService.apiRolesIdRestrictionsGet(roleId));

    const marketRestriction = roleRestrictions.restrictions?.find((restriction: any) =>
      restriction === Restrictions.MarketsRestriction
    );
    const retailerRestriction = roleRestrictions.restrictions?.find((restriction: any) =>
      restriction === Restrictions.RetailerRestriction
    );

    this.isMarketsFieldDisabled = !marketRestriction;
    this.isRetailersFieldDisabled = !retailerRestriction;

    if (this.isMarketsFieldDisabled) {
      this.userForm.get('marketIds')?.clearValidators();
    }

    if (this.isRetailersFieldDisabled) {
      this.userForm.get('retailerIds')?.clearValidators();
    }

    this.userForm.get('retailerIds')?.updateValueAndValidity();
    this.userForm.get('marketIds')?.updateValueAndValidity();
  }

  async onRoleChange(event: any) {
    const selectedOption = event.selected;

    this.userForm.patchValue({
      marketIds: [],
      retailerIds: []
    });

    // reset touch state
    this.userForm.get('marketIds')?.markAsPristine();
    this.userForm.get('retailerIds')?.markAsPristine();

    if (!selectedOption) {
      const rolesControl = this.userForm.get('roleId');
      rolesControl?.setValue(null);
      rolesControl?.markAsTouched();
      rolesControl?.setErrors({ required: true });
    }
    else {
      const rolesControl = this.userForm.get('roleId');
      rolesControl?.markAsDirty();
      rolesControl?.setErrors(null);

      this.updateValidatorsBasedOnRoles(selectedOption);
    }
  }

  isRoleSelected(): boolean {
    return (this.userForm.get('roleId')?.value ?? []).length > 0;
  }

  isSubmitDisabled(): boolean {
    const formInvalid = this.userForm.invalid;
    return formInvalid;
  }

  async submitForm() {
    if (this.userForm.valid) {
      const selectedLanguageCode = this.userForm.controls['selectedLanguage'].value?.[0] ?? null;

      const selectedLanguage: any = this.languages.find((lang: any) => lang.code === selectedLanguageCode);

      const payload: UserRequest = {
        email: this.userForm.controls['email'].value,
        firstName: this.userForm.controls['firstname'].value,
        lastName: this.userForm.controls['lastname'].value,
        romanisedFirstName: this.userForm.controls['romanisedFirstName'].value,
        romanisedLastName: this.userForm.controls['romanisedLastName'].value,
        languageId: selectedLanguage?.id ?? null,
        username: this.userForm.controls['username'].value,
        allowedChangePassword: this.userForm.controls['allowPasswordChange'].value === 'true' ? true : false,
        roleId: this.userForm.controls['roleId'].value?.[0],
        marketIds: this.userForm.controls['marketIds'].value || [],
        retailerIds: this.userForm.controls['retailerIds'].value || []
      };

      await firstValueFrom(this.usersService.apiUsersCreatePost(payload));

      this.userForm.controls['email'].setValue('');
      this.userForm.controls['firstname'].setValue('');
      this.userForm.controls['lastname'].setValue('');
      this.userForm.controls['romanisedFirstName'].setValue('');
      this.userForm.controls['romanisedLastName'].setValue('');
      this.userForm.controls['username'].setValue('');
      this.userForm.controls['allowPasswordChange'].setValue(null);
      this.userForm.controls['roleId'].setValue([]);
      this.userForm.controls['marketIds'].setValue([]);
      this.userForm.controls['retailerIds'].setValue([]);
      this.userForm.controls['selectedLanguage'].setValue([]);

      this.router.navigate(['/users']);
    }
  }
}
